import React, {useEffect, useState} from "react";
import {Palette, X} from "lucide-react";
import {Design, designs} from "./FirstSFGData";

interface props {
    design: Design
    onSwitch: () => void
    raised: boolean
}

const HINT_KEY = 'sfg-design-hint-dismissed'

const readHintDismissed = () => {
    try {
        return window.localStorage.getItem(HINT_KEY) === '1'
    } catch {
        return false
    }
}

const FirstSFGDesignSwitcher = ({design, onSwitch, raised}: props) => {
    const [isHintOpen, setIsHintOpen] = useState(false)
    const [hasSeenHint, setHasSeenHint] = useState(readHintDismissed)
    const index = designs.findIndex(option => option.id === design)
    const next = designs[(index + 1) % designs.length]

    useEffect(() => {
        if (hasSeenHint) return
        const openTimer = window.setTimeout(() => setIsHintOpen(true), 1200)
        const closeTimer = window.setTimeout(() => setIsHintOpen(false), 12000)
        return () => {
            window.clearTimeout(openTimer)
            window.clearTimeout(closeTimer)
        }
        // Only schedule the hint once, on first mount.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const dismissHint = () => {
        setIsHintOpen(false)
        setHasSeenHint(true)
        try {
            window.localStorage.setItem(HINT_KEY, '1')
        } catch {
            // Storage can be unavailable (private mode); the hint simply shows again next visit.
        }
    }

    const handleSwitch = () => {
        dismissHint()
        onSwitch()
    }

    return (
        <div className={`sfg-switcher ${raised ? 'raised' : ''}`}>
            {isHintOpen &&
                <div className={'sfg-switcher-hint'} role={'status'}>
                    <p><strong>This page comes in {designs.length} designs.</strong> Tap below to see the {next.name.toLowerCase()} version.</p>
                    <button className={'sfg-switcher-hint-close'} onClick={dismissHint} aria-label={'Dismiss tip'}>
                        <X size={14} aria-hidden={true}/>
                    </button>
                </div>
            }
            <button
                className={`sfg-switcher-button ${hasSeenHint ? '' : 'attention'}`}
                onClick={handleSwitch}
                aria-label={`Switch to the ${next.name} design`}
            >
                <span className={'sfg-switcher-swatch'} aria-hidden={true}>
                    <span style={{backgroundColor: next.swatch[0]}}/>
                    <span style={{backgroundColor: next.swatch[1]}}/>
                </span>
                <span className={'sfg-switcher-text'}>
                    <span className={'sfg-switcher-label'}>
                        <Palette size={12} aria-hidden={true}/> Design {index + 1} of {designs.length}
                    </span>
                    <span className={'sfg-switcher-next'}>Try {next.name} →</span>
                </span>
            </button>
        </div>
    )
}
export default FirstSFGDesignSwitcher
